<script>
  import QuantityModal from '../QuantityModal/index.svelte'

  // Quantity card for the item show page. Shows remaining / initial, a mini
  // dot preview (gray = available, green = used), a low-stock cue, and an
  // expand affordance. Clicking the card opens the use-up modal.
  //
  // Containers are not consumable: they always show a plain "1" and the modal
  // never opens.
  let { item } = $props()

  const isContainer = $derived(!!item?.container_type)
  const initial = $derived(item?.initial_quantity ?? 1)
  const remaining = $derived(item?.remaining_quantity ?? 1)
  const used = $derived(item?.used_quantity ?? Math.max(0, initial - remaining))
  const lowStock = $derived(!!item?.low_stock)

  const MINI_CAP = 10
  // Mini dots: available (gray) first, then used (green), proportional to cap.
  const miniDots = $derived.by(() => {
    const show = Math.min(initial, MINI_CAP)
    const availShown = Math.round((remaining / initial) * show)
    return Array.from({ length: show }, (_, i) => ({
      available: i < availShown,
      faded: initial > MINI_CAP && i >= show - 4
    }))
  })

  let modalOpen = $state(false)
</script>

{#if isContainer}
  <!-- Containers are always a single, non-consumable unit. -->
  <div class="bg-white border border-gray-200 rounded-xl px-4 py-3.5 shadow-sm">
    <div class="text-[11px] uppercase tracking-wide font-bold text-gray-500">Quantity</div>
    <div class="text-xl font-extrabold text-gray-900 mt-1">1</div>
  </div>
{:else}
  <button
    type="button"
    onclick={() => (modalOpen = true)}
    class="relative w-full text-left bg-white border rounded-xl px-4 py-3.5 shadow-sm transition hover:-translate-y-px {lowStock ? 'border-amber-300' : 'border-gray-200 hover:border-brand-300'}"
  >
    <div class="text-[11px] uppercase tracking-wide font-bold text-gray-500">Quantity</div>

    <!-- expand affordance, top-right -->
    <span class="absolute top-3 right-3 w-[26px] h-[26px] rounded-md grid place-items-center border border-gray-200 bg-gray-50 text-gray-400">
      <svg class="w-[15px] h-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>
    </span>

    <div class="flex items-center gap-3 mt-1">
      <span class="text-2xl font-extrabold text-gray-900 leading-none">
        {remaining}{#if initial !== remaining || used > 0}<span class="text-sm font-semibold text-gray-400"> / {initial}</span>{/if}
      </span>
      <span class="flex items-center gap-1 flex-wrap">
        {#each miniDots as dot}
          <span
            class="w-[11px] h-[11px] rounded-full {dot.available
              ? 'bg-white border-[1.5px] border-gray-300'
              : 'bg-brand-500 border border-brand-600'}"
            style={dot.faded ? 'opacity: 0.4;' : ''}
          ></span>
        {/each}
        {#if initial > MINI_CAP}
          <span class="text-[11px] text-gray-400 font-semibold ml-0.5">+{initial - MINI_CAP}</span>
        {/if}
      </span>
    </div>

    <div class="mt-2 text-xs">
      {#if lowStock}
        <span class="inline-flex items-center gap-1 font-semibold text-amber-700">
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
          Low stock — {used} used of {initial}
        </span>
      {:else if used > 0}
        <span class="text-gray-500"><b class="text-brand-700 font-semibold">{used} used</b> of {initial}</span>
      {:else}
        <span class="text-gray-400">None used</span>
      {/if}
    </div>
  </button>

  <QuantityModal open={modalOpen} {item} onClose={() => (modalOpen = false)} />
{/if}
